import React, { useCallback, useRef, type ReactNode } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Text } from '@atlaskit/primitives';
import { type FlagId, toFlagId, useFlagsService } from '@atlassian/jira-flags';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import Value from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/value';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE,
	ENABLED,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants';
import { usePathParam, useRouterActions } from '@atlassian/react-resource-router';
import { FeatureToggleTile } from '../../../../../../common/ui/feature-toggle-tile/index.tsx';
import useSaveFailedFlag from '../../../../../../controllers/common/save-failed-flag/index.tsx';
import useServiceProjectFeaturesController from '../../../../../../controllers/common/service-project-features-controller/index.tsx';
import messages from './messages';

type Props = {
	isJiraAdmin: boolean;
	isCustomerContextEnabled: boolean;
};

const ProductsAndEntitlementsFeatureTile = ({ isJiraAdmin, isCustomerContextEnabled }: Props) => {
	const { formatMessage } = useIntl();
	const { data, saving, update } = useServiceProjectFeaturesController();
	const showSaveErrorFlag = useSaveFailedFlag();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag, dismissFlag } = useFlagsService();
	const { push } = useRouterActions();

	const [projectKey = ''] = usePathParam('projectKey');
	const flagIdRef = useRef<{ [key: string]: FlagId }>({});

	const featureInProductHelpArticleId = '5vAddFwJAdYXOuso7OdwMz';
	const featureFallbackArticleUrl =
		'https://support.atlassian.com/jira-service-management-cloud/docs/create-product-entitlements-for-your-customers/';
	const featureHelpArticleFireAnalytics = () =>
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'helpLink clicked',
			'learnMoreAboutProductsAndEntitlements',
			{
				featureInProductHelpArticleId,
			},
		);
	const featureFallbackAnchor = (
		<Button
			appearance="link"
			spacing="none"
			href={featureFallbackArticleUrl}
			target="_blank"
			onClick={featureHelpArticleFireAnalytics}
		>
			{formatMessage(messages.featureLearnMore)}
		</Button>
	);
	const featureInProductHelpLink = (
		<HelpPanelLink
			appearance="link"
			spacing="none"
			articleId={featureInProductHelpArticleId}
			fallbackComponent={featureFallbackAnchor}
			onClick={featureHelpArticleFireAnalytics}
		>
			{formatMessage(messages.featureLearnMore)}
		</HelpPanelLink>
	);

	const updateFeatureToggle = useCallback(
		(enabled: boolean) => {
			update({
				feature: CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE,
				enabled,
				onSuccess: () => {
					const flagIdKey = `${CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE}-service-feature-success`;
					const { current: flagIds } = flagIdRef;
					// toFlagId creates a new string reference. But we want to reuse
					// flag ids so the most relevant flag for a feature is shown
					flagIds[flagIdKey] = flagIds[flagIdKey] || toFlagId(flagIdKey);

					const id = flagIds[flagIdKey];
					const onClick = () => {
						dismissFlag(id);
					};
					// Dismiss previous flag with the same id. So user cannot
					// interact with out of update flags
					dismissFlag(id);

					showFlag({
						id,
						type: 'success',
						isAutoDismiss: !enabled,
						title: [messages.featureContactSuccessTitle, { value: enabled }],
						description: [
							enabled
								? messages.featureSuccessEnableDescription
								: messages.featureSuccessDisableDescription,
							{
								strong: (children: ReactNode) => <Text weight="bold">{children}</Text>,
							},
						],
						actions: enabled
							? [
									{
										content: messages.featureSuccessEnableAction,
										onClick: () => {
											dismissFlag(id);
											push(`/jira/servicedesk/projects/${projectKey}/products/`);
										},
									},
								]
							: [
									{
										content: messages.featureSuccessDisableDismiss,
										onClick,
									},
								],
					});
				},
				onFail: () => {
					showSaveErrorFlag(formatMessage(messages.featureTitle), enabled);
				},
			});
		},
		[dismissFlag, projectKey, push, showFlag, showSaveErrorFlag, update, formatMessage],
	);

	const createFeatureOnChangeHandler = useCallback(
		() => (feature: string, enabled: boolean, analyticEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticEvent, 'settingsToggle', { field: feature });

			updateFeatureToggle(enabled);
		},
		[updateFeatureToggle],
	);

	const isProductsAndEntitlementsEnabled =
		data?.[CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE]?.status === ENABLED;
	const nonJiraAdmintooltip = isJiraAdmin ? null : formatMessage(messages.featureContactAdmin);
	const customerContextDisablesTooltip = isCustomerContextEnabled ? null : (
		<FormattedMessage
			{...messages.featurePrerequisite}
			values={{
				strong: (chunks) => <Text weight="bold">{chunks}</Text>,
			}}
		/>
	);
	return (
		<>
			<FeatureToggleTile
				feature={CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE}
				title={formatMessage(messages.featureTitle)}
				value={isProductsAndEntitlementsEnabled}
				saving={saving[CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE]}
				onChange={createFeatureOnChangeHandler()}
				tooltip={nonJiraAdmintooltip || customerContextDisablesTooltip}
				disabled={!isJiraAdmin || !isCustomerContextEnabled}
				illustration={<Value alt="" />}
			>
				<FormattedMessage
					{...messages.featureDescription}
					values={{
						learnMoreLink: featureInProductHelpLink,
					}}
				/>
			</FeatureToggleTile>
		</>
	);
};

export default ProductsAndEntitlementsFeatureTile;
